import React, {useMemo, useState} from 'react';
import {useDisclosure} from '@mantine/hooks';
import type {MRT_ColumnDef} from 'mantine-react-table';
import type {SkillResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {
  useDeleteSkillMutation,
  useGetSkillListQuery,
} from 'src/redux/queries/skill';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {SkillFormModal} from './SkillFormModal';

export interface SkillsTabProps {
  /** Training:Update — CRUD actions are hidden (not disabled) without it. */
  canManage: boolean;
}

/**
 * Skills CRUD — the catalog behind sign-offs. Soft delete with confirm.
 */
export const SkillsTab: React.FC<SkillsTabProps> = ({canManage}) => {
  const {setAlert} = useAlert();
  const [editingSkill, setEditingSkill] = useState<SkillResponse | undefined>();
  const [formOpened, formHandlers] = useDisclosure(false);
  const [deleteTarget, setDeleteTarget] = useState<SkillResponse | undefined>();

  const {data, isLoading, isFetching, isError, refetch} = useGetSkillListQuery({
    start: 0,
    length: 500,
  });
  const [deleteSkill, {isLoading: isDeleting}] = useDeleteSkillMutation();

  const rows = data?.data ?? [];

  const handleCreate = () => {
    setEditingSkill(undefined);
    formHandlers.open();
  };

  const handleEdit = (skill: SkillResponse) => {
    setEditingSkill(skill);
    formHandlers.open();
  };

  const handleDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await deleteSkill(deleteTarget.id).unwrap();
      setAlert(`${deleteTarget.name} was successfully deleted.`, 'success');
      setDeleteTarget(undefined);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const columns = useMemo<MRT_ColumnDef<SkillResponse>[]>(
    () => [
      {accessorKey: 'name', header: 'Name'},
      {accessorKey: 'description', header: 'Description'},
      {
        accessorKey: 'criteria',
        header: '# Criteria',
        accessorFn: row => row.criteria?.length ?? 0,
      },
      {
        accessorKey: 'expiryPeriodDays',
        header: 'Expires After (days)',
        Cell: ({cell}) => cell.getValue<number | undefined>() ?? 'Never',
      },
      {
        accessorKey: 'active',
        header: 'Active',
        Cell: ({cell}) => (cell.getValue<boolean>() ? 'Active' : 'Inactive'),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'training-skills',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 20},
  });

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: false,
      manualFiltering: false,
      manualPagination: false,
      enableRowActions: canManage,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.Edit onClick={() => handleEdit(row.original)} />
          <TableRowActions.Menu>
            <TableRowActions.MenuItem
              color="red"
              onClick={() => setDeleteTarget(row.original)}>
              Delete
            </TableRowActions.MenuItem>
          </TableRowActions.Menu>
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          {canManage && (
            <TableToolbar.Actions>
              <TableToolbar.Action variant="create" onClick={handleCreate}>
                New Skill
              </TableToolbar.Action>
            </TableToolbar.Actions>
          )}
        </TableToolbar>
      ),
    },
    totalCount: rows.length,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <>
      {table}

      {canManage && (
        <SkillFormModal
          opened={formOpened}
          onClose={formHandlers.close}
          skill={editingSkill}
        />
      )}

      <ConfirmDialog
        opened={deleteTarget !== undefined}
        onClose={() => setDeleteTarget(undefined)}
        onConfirm={handleDelete}
        title="Delete skill?"
        body={`"${deleteTarget?.name}" will be removed. Existing completed sign-off records are kept for history, but any pending requests for this skill are hard-deleted along with it.`}
        confirmationButtonColor="red"
        buttonTextYes="Delete"
        confirmationButtonDisabled={isDeleting}
      />
    </>
  );
};
